/**
 * Listing System - the "living market" engine.
 * Keeps exactly LISTING_CONFIG.listedSlots coins listed (all permanent majors +
 * rotating meme coins). Meme coins are delisted (floored/stale/aged out),
 * rest in the graveyard, then may revive as a "{name} 2.0" rebrand.
 * One transition per turn, spaced by transitionCooldown; delist conditions are
 * re-checked every evalInterval. Delisting force-liquidates the holder position.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { COINS, getCoinConfig } from '../config/coins.js';
import { LISTING_CONFIG } from '../config/listing.js';
import { NEWS_CONFIG } from '../config/coinNews.js';
import {
  createCoinMarket, minimalCoinMarket, getChange24h,
  type CoinListing, type ExchangeState,
} from '../exchangeState.js';
import type { FeedNewsItem } from '../infoState.js';
import { freshWhale } from '../infoState.js';
import { drawSeededComments } from '../config/comments.js';
import { freshRug, freshScenario } from '../riskState.js';

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function makeId(now: number): string {
  return `${now}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Prepend a listing news item (list/delist/revive) to the feed. */
function publishListingNews(state: GameState, newsId: string, coinId: string, dir: 1 | -1, now: number): GameState {
  if (!state.info) return state;
  const drawn = drawSeededComments(state.info.bags, dir, now);
  const item: FeedNewsItem = {
    kind: 'news', instanceId: makeId(now), newsId, coinId, direction: dir, publishedAt: now,
    seeded: drawn.comments,
  };
  return {
    ...state,
    info: {
      ...state.info, bags: drawn.bags,
      feed: [item, ...state.info.feed].slice(0, NEWS_CONFIG.feedLimit),
    },
  };
}

/** Reset per-coin risk / whale state to a neutral fresh baseline. */
function resetCoinDynamics(state: GameState, coinId: string, startPrice: number, now: number): GameState {
  const next = { ...state };
  if (next.whales?.[coinId]) {
    next.whales = { ...next.whales, [coinId]: freshWhale(startPrice, now) };
  }
  if (next.risk) {
    next.risk = {
      ...next.risk,
      scenarios: { ...next.risk.scenarios, [coinId]: freshScenario(now) },
      rugs: { ...next.risk.rugs, [coinId]: freshRug() },
    };
  }
  return next;
}

// ===== DELIST =====

/** True when a listed meme coin currently qualifies for delisting (pure). */
export function shouldDelist(state: GameState, coinId: string, now: number): boolean {
  const cfg = getCoinConfig(coinId);
  const listing = state.exchange.listing[coinId];
  const coin = state.exchange.coins[coinId];
  if (!cfg || cfg.permanent || !coin || listing?.status !== 'listed') return false;
  if (now - listing.listedAt < LISTING_CONFIG.minListedAge) return false;

  // Floored: price sat near the hard floor while a rug held it there.
  const nearFloor = coin.price <= cfg.minPrice * LISTING_CONFIG.delistFloorRatio;
  const rug = state.risk?.rugs[coinId];
  const flooredLongEnough = rug?.phase === 'floor' && now - rug.startedAt >= LISTING_CONFIG.delistFloorTime;
  if (nearFloor && flooredLongEnough) return true;

  // Dead volume: 24h change stayed tiny for a long while.
  const stale = Math.abs(getChange24h(coin)) <= LISTING_CONFIG.delistStalePct;
  const oldEnough = now - listing.listedAt >= LISTING_CONFIG.delistStaleTime;
  if (stale && oldEnough && Math.random() < LISTING_CONFIG.delistRandomChance) return true;

  // Aging out: rare pure-random churn so the market keeps moving.
  return oldEnough && Math.random() < LISTING_CONFIG.delistRandomChance * 0.5;
}

/** Delist a coin: liquidate the holding, mark delisted, publish news, reset dynamics. */
export function delistCoin(state: GameState, coinId: string, now: number): GameState {
  const listing = state.exchange.listing[coinId];
  const coin = state.exchange.coins[coinId];
  const cfg = getCoinConfig(coinId);
  if (!listing || !coin || !cfg || cfg.permanent || listing.status !== 'listed') return state;

  let coins = state.coins;
  let totalEarned = state.statistics.totalEarned;
  const holdings = { ...state.exchange.holdings };
  const holding = holdings[coinId];
  if (holding) {
    const payout = holding.amount * coin.price * LISTING_CONFIG.liquidationRatio;
    coins += payout;
    totalEarned += payout;
    delete holdings[coinId];
  }

  const nextListing: CoinListing = {
    ...listing, status: 'delisted', delistedAt: now, everDelisted: true,
  };
  const exchange: ExchangeState = {
    ...state.exchange,
    coins: { ...state.exchange.coins, [coinId]: minimalCoinMarket(coin.price) },
    listing: { ...state.exchange.listing, [coinId]: nextListing },
    holdings,
    lastListingTransition: now,
  };
  let next: GameState = {
    ...state, coins, exchange,
    statistics: { ...state.statistics, totalEarned },
  };
  next = resetCoinDynamics(next, coinId, cfg.startPrice, now);
  return publishListingNews(next, 'list_delist', coinId, -1, now);
}

// ===== LIST / REVIVE =====

/** Meme coins eligible to be (re)listed: pool coins + rested delisted coins. */
export function listableCoinIds(state: GameState, now: number): string[] {
  return COINS.filter(c => {
    if (c.permanent) return false;
    const l = state.exchange.listing[c.id];
    if (!l) return false;
    if (l.status === 'pool') return true;
    if (l.status === 'delisted') return now - l.delistedAt >= LISTING_CONFIG.reviveDelay;
    return false;
  }).map(c => c.id);
}

/** List (or revive) one coin into an open slot: seed market + dynamics + news. */
export function listCoin(state: GameState, coinId: string, now: number): GameState {
  const listing = state.exchange.listing[coinId];
  const cfg = getCoinConfig(coinId);
  if (!listing || !cfg || listing.status === 'listed') return state;

  const reviving = listing.status === 'delisted';
  const startPrice = Math.max(
    cfg.minPrice,
    cfg.startPrice * randRange(LISTING_CONFIG.listStartJitterMin, LISTING_CONFIG.listStartJitterMax)
  );
  const nextListing: CoinListing = {
    ...listing,
    status: 'listed',
    listedAt: now,
    delistedAt: 0,
    revivedCount: reviving ? listing.revivedCount + 1 : listing.revivedCount,
  };
  const exchange: ExchangeState = {
    ...state.exchange,
    coins: { ...state.exchange.coins, [coinId]: createCoinMarket(startPrice, now) },
    listing: { ...state.exchange.listing, [coinId]: nextListing },
    lastListingTransition: now,
  };
  let next: GameState = { ...state, exchange };
  next = resetCoinDynamics(next, coinId, startPrice, now);
  return publishListingNews(next, reviving ? 'list_revive' : 'list_new', coinId, 1, now);
}

/** Count of currently listed coins. */
function listedCount(state: GameState): number {
  return Object.values(state.exchange.listing).filter(l => l.status === 'listed').length;
}

/** Pick a listable coin, preferring revivals so graveyard coins come back. */
function pickToList(state: GameState, now: number): string | null {
  const ids = listableCoinIds(state, now);
  if (ids.length === 0) return null;
  const revivable = ids.filter(id => state.exchange.listing[id]?.status === 'delisted');
  if (revivable.length > 0 && Math.random() < LISTING_CONFIG.reviveChance) {
    return revivable[Math.floor(Math.random() * revivable.length)];
  }
  const pool = ids.filter(id => state.exchange.listing[id]?.status === 'pool');
  const source = pool.length > 0 ? pool : ids;
  return source[Math.floor(Math.random() * source.length)];
}

/** Pick a listed meme coin that qualifies for delisting (or null). */
function pickToDelist(state: GameState, now: number): string | null {
  const eligible = COINS
    .filter(c => !c.permanent && state.exchange.listing[c.id]?.status === 'listed')
    .map(c => c.id)
    .filter(id => shouldDelist(state, id, now));
  return eligible.length > 0 ? eligible[Math.floor(Math.random() * eligible.length)] : null;
}

export const listingSystem: TickSystem<GameState, GameAction> = {
  name: 'listing',

  update(state) {
    if (!state.isRunning || !state.exchange) return state;
    const now = Date.now();

    // Always keep the slots full first (fast path, ignores cooldown when short).
    if (listedCount(state) < LISTING_CONFIG.listedSlots) {
      const id = pickToList(state, now);
      if (id) return listCoin(state, id, now);
    }

    // Throttle churn: one delist->relist cycle per transition cooldown / eval tick.
    if (now - state.exchange.lastListingEval < LISTING_CONFIG.evalInterval) return state;
    if (now - state.exchange.lastListingTransition < LISTING_CONFIG.transitionCooldown) {
      return { ...state, exchange: { ...state.exchange, lastListingEval: now } };
    }

    const withEval: GameState = { ...state, exchange: { ...state.exchange, lastListingEval: now } };
    const delistId = pickToDelist(withEval, now);
    if (delistId) return delistCoin(withEval, delistId, now);
    return withEval;
  },
};
